import type { TFunction } from "i18next";

export interface QuotaViolation {
  kind: "storage" | "fileCount" | "monthlyIngest";
  message: string;
  data: Record<string, unknown>;
}

export interface QuotaErrorPayload {
  type?: string;
  data?: { violations?: QuotaViolation[] };
}

type SizeFormatter = (bytes: number) => string;
type DurationFormatter = (milliseconds: number) => string;

/**
 * Builds a user-facing, localized message from a backend `quota_exceeded`
 * error. The backend's violation `kind` is the stable key; its English
 * `message` is debug-only and never shown. Byte values go through the size
 * formatter, monthly-ingest seconds through the duration formatter (which
 * expects milliseconds).
 */
export function buildQuotaErrorMessage(
  error: QuotaErrorPayload | undefined,
  t: TFunction,
  formatSize: SizeFormatter,
  formatDuration: DurationFormatter
): string {
  const generic = () => t("pages:sessions.upload.video.errorQuotaExceeded");
  const violations = error?.data?.violations ?? [];

  if (violations.length === 0) {
    return generic();
  }

  const parts = violations.map((violation) => {
    switch (violation.kind) {
      case "storage":
        return t("pages:sessions.upload.video.errorQuotaStorage", {
          used: formatSize(Number(violation.data.usedBytes ?? 0)),
          quota: formatSize(Number(violation.data.quotaBytes ?? 0)),
        });
      case "fileCount":
        return t("pages:sessions.upload.video.errorQuotaFileCount", {
          used: Number(violation.data.used ?? 0),
          quota: Number(violation.data.quota ?? 0),
        });
      case "monthlyIngest":
        return t("pages:sessions.upload.video.errorQuotaMonthlyIngest", {
          uploaded: formatDuration(
            Number(violation.data.uploadedSeconds ?? 0) * 1000
          ),
          limit: formatDuration(
            Number(violation.data.monthlyLimitSeconds ?? 0) * 1000
          ),
        });
      default:
        return generic();
    }
  });

  return parts.join(" ");
}
